import { useId, useMemo, useRef, useState } from 'react'
import { ChevronRight, ChevronUp, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Typography, typographyVariants } from '@/components/ui/typography'
import {
  BoardEditContext,
  useBoardEdit,
  type BoardEdit,
} from '@/features/ogsm/board-edit-context'
import { ogsmErrorMessage } from '@/features/ogsm/errors'
import { BoardReorder } from '@/components/custom/ogsm/board-reorder'
import { HiddenStrategiesChip } from '@/components/custom/ogsm/inactive-toggle'
import { InlineText } from '@/components/custom/ogsm/inline-text'
import { NewObjectiveDialog } from '@/components/custom/ogsm/new-objective-dialog'
import { ReviseTargetDialog } from '@/components/custom/ogsm/revise-target-dialog'
import {
  BOARD_COLUMNS,
  ObjectiveBand,
} from '@/components/custom/ogsm/objective-band'
import { useFocusFallback } from '@/hooks/use-focus-fallback'
import { visibleNodes } from '@/features/ogsm/inactive'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { findNode } from '@/features/ogsm/reorder'
import { drilledPath, findDrilledObjective } from '@/features/ogsm/search'
import { StatusLegend } from '@/components/custom/ogsm/status-mark'
import { TargetCell } from '@/components/custom/ogsm/target-cell'
import { useOgsmApi } from '@/hooks/use-ogsm'
import {
  useMoveOgsmNodeMutation,
  useUpdateOgsmNodeMutation,
  useUpdateOgsmStatementMutation,
} from '@/hooks/use-ogsm-data'
import { useIsClamped } from '@/hooks/use-is-clamped'
import { useOgsmShowInactive } from '@/hooks/use-ogsm-show-inactive'
import { useDroppedBoard } from '@/hooks/use-ogsm-sortable'
import { cn } from '@/lib/utils'
import type { OgsmBoard, OgsmBoardNode } from '@/types/api'

export interface OgsmBoardViewProps {
  companyId: string
  board: OgsmBoard
  objectiveId?: string
  onDrill: (objectiveId: string | undefined) => void
  /** Opens the new-objective dialog; offered at the root of an editable session. */
  onAddObjective?: () => void
}

function ColumnHeader() {
  const { columns } = OGSM_COPY
  const cells = [
    { letter: 'O', label: columns.objectives, className: '' },
    {
      letter: 'G',
      label: columns.goals,
      className: 'border-l border-primary-foreground/20',
    },
    {
      letter: 'S',
      label: columns.strategies,
      className: 'border-l border-primary-foreground/45',
    },
    {
      letter: 'M',
      label: columns.measures,
      className:
        'col-start-5 justify-end border-l border-primary-foreground/20',
    },
  ]
  return (
    <div
      className={cn(
        'hidden border-l-4 border-l-transparent bg-primary text-primary-foreground lg:grid',
        BOARD_COLUMNS
      )}
    >
      <div />
      {cells.map(({ letter, label, className }, position) => (
        <div
          key={position}
          className={cn('flex items-center gap-2 px-3 py-2.5', className)}
        >
          <Typography
            as="span"
            variant="overline"
            className="inline-flex size-4 items-center justify-center rounded border border-primary-foreground/45 text-[9px]"
          >
            {letter}
          </Typography>
          <Typography as="span" variant="overline" className="tracking-wider">
            {label}
          </Typography>
        </div>
      ))}
      <div className="border-l border-primary-foreground/20" />
    </div>
  )
}

/** Two lines at most; the toggle shows only when the text is really cut. */
function StatementText({
  text,
  expanded,
  onToggle,
}: {
  text: string
  expanded: boolean
  onToggle: () => void
}) {
  const [measure, clamped] = useIsClamped()
  const id = useId()
  return (
    <div className="flex flex-col items-start gap-1">
      <div
        key={text}
        id={id}
        ref={measure}
        className={cn(
          typographyVariants({ variant: 'body' }),
          'break-words text-primary',
          !expanded && 'line-clamp-2'
        )}
      >
        {text}
      </div>
      {(clamped || expanded) && (
        <Button
          variant="link"
          className="h-auto p-0 text-xs"
          aria-expanded={expanded}
          aria-controls={id}
          onClick={onToggle}
        >
          {expanded ? OGSM_COPY.showLess : OGSM_COPY.showMore}
        </Button>
      )}
    </div>
  )
}

function Statement({ statement }: { statement: string | null | undefined }) {
  const { companyId, session } = useBoardEdit()
  const save = useUpdateOgsmStatementMutation(useOgsmApi(companyId), session.id)
  // Held here so an edit, which swaps the text for the editor, keeps it.
  const [expanded, setExpanded] = useState(false)
  // New text is measured afresh: a saved short text must not keep "Show less".
  const [seen, setSeen] = useState(statement)
  if (seen !== statement) {
    setSeen(statement)
    setExpanded(false)
  }
  return (
    <div className="flex flex-col gap-1">
      <InlineText
        value={statement}
        label={OGSM_COPY.statementLabel}
        placeholder={OGSM_COPY.addStatement}
        menuLabel={OGSM_COPY.actions(OGSM_COPY.statementLabel)}
        editLabel={OGSM_COPY.editStatement}
        disabled={!session.structureEditable}
        onSave={(next) => save.mutateAsync(next)}
        onCancel={() => save.reset()}
      >
        {(text) => (
          <StatementText
            text={text}
            expanded={expanded}
            onToggle={() => setExpanded((open) => !open)}
          />
        )}
      </InlineText>
      {save.error && (
        <Typography variant="body-sm" role="alert" className="text-destructive">
          {ogsmErrorMessage(save.error)}
        </Typography>
      )}
    </div>
  )
}

export function OgsmBoardView({
  companyId,
  board: cached,
  objectiveId,
  onDrill,
  onAddObjective,
}: OgsmBoardViewProps) {
  const { board, drop } = useDroppedBoard(cached)
  const { session } = board
  const api = useOgsmApi(companyId)
  const update = useUpdateOgsmNodeMutation(api, session.id)
  const move = useMoveOgsmNodeMutation(api, session.id)
  const [editing, setEditing] = useState<OgsmBoardNode | null>(null)
  const [revisingId, setRevisingId] = useState<string | null>(null)
  const revising = revisingId ? findNode(board, revisingId) : null
  const { showInactive } = useOgsmShowInactive()
  const footerChip = useRef<HTMLButtonElement>(null)
  const focusFallback = useFocusFallback(footerChip)
  const edit = useMemo<BoardEdit>(
    () => ({
      companyId,
      session,
      saveNode: (nodeId, input) => update.mutate({ nodeId, input }),
      errorFor: (nodeId) => {
        if (update.isError && update.variables?.nodeId === nodeId) {
          return ogsmErrorMessage(update.error)
        }
        return move.isError && move.variables?.nodeId === nodeId
          ? ogsmErrorMessage(move.error)
          : null
      },
      editNode: setEditing,
    }),
    [companyId, session, update, move]
  )
  const published = session.status === 'PUBLISHED'
  const parent = findDrilledObjective(board, objectiveId)
  const ancestors = drilledPath(board, objectiveId).slice(0, -1)
  // Hiding is render-only: `BoardReorder` still positions over the full `board`.
  const bands = visibleNodes(
    parent ? parent.strategies : board.objectives,
    showInactive
  )

  return (
    <BoardEditContext.Provider value={edit}>
      <div className="flex flex-col gap-4">
        {parent ? (
          <section
            aria-label={OGSM_COPY.parentObjective}
            className="flex flex-wrap items-center gap-4 rounded-md border border-primary/20 bg-primary/5 px-4 py-2"
          >
            <Typography variant="overline" className="text-primary/60">
              {OGSM_COPY.parentObjective}
            </Typography>
            <ol className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
              {ancestors.map((ancestor) => (
                <li
                  key={ancestor.id}
                  className="flex min-w-0 items-center gap-2"
                >
                  <Button
                    variant="link"
                    className="h-auto min-w-0 p-0"
                    onClick={() => onDrill(ancestor.id)}
                  >
                    <Typography
                      as="span"
                      variant="h6"
                      title={ancestor.title}
                      className="block max-w-48 truncate text-primary/60 hover:text-primary"
                    >
                      {ancestor.title}
                    </Typography>
                  </Button>
                  <ChevronRight
                    aria-hidden
                    className="size-3.5 shrink-0 text-primary/60"
                  />
                </li>
              ))}
              <li className="min-w-0">
                <Typography
                  variant="h6"
                  className="min-w-0 break-words leading-snug"
                >
                  {parent.title}
                </Typography>
              </li>
            </ol>
            <TargetCell node={parent} layout="inline" />
            <Button
              variant="secondary-tint"
              size="badge"
              onClick={() => onDrill(parent.parentId ?? undefined)}
            >
              <ChevronUp />
              {OGSM_COPY.upOneLevel}
            </Button>
          </section>
        ) : (
          <Statement statement={session.statement} />
        )}

        <div
          className="overflow-hidden rounded-md border border-primary/20"
          {...focusFallback}
        >
          <ColumnHeader />
          {parent && parent.strategies.length === 0 && (
            <Typography variant="muted" className="px-4 py-8 text-center">
              {session.structureEditable
                ? OGSM_COPY.drilledEmpty(drilledKind(parent))
                : OGSM_COPY.drilledEmptyReadOnly(drilledKind(parent))}
            </Typography>
          )}
          <BoardReorder
            board={board}
            bandIds={bands.map((band) => band.id)}
            onMove={(next) => {
              drop(next)
              move.mutate(next)
            }}
          >
            {bands.map((band) => (
              <ObjectiveBand
                key={band.id}
                node={band}
                published={published}
                onDrill={onDrill}
              />
            ))}
          </BoardReorder>
          {parent && parent.strategiesInactive > 0 && (
            <div className="flex items-center border-t border-t-primary/45 px-3 py-2 lg:pl-14">
              <HiddenStrategiesChip
                count={parent.strategiesInactive}
                ref={footerChip}
              />
            </div>
          )}
        </div>

        {onAddObjective && !parent && session.structureEditable && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-8 self-start text-primary/60"
            onClick={onAddObjective}
          >
            <Plus className="size-3" />
            {OGSM_COPY.addObjective}
          </Button>
        )}

        <StatusLegend />
      </div>
      <NewObjectiveDialog
        companyId={companyId}
        session={session}
        target={editing ? { mode: 'edit', node: editing } : null}
        onClose={() => setEditing(null)}
        onRevise={(node) => setRevisingId(node.id)}
      />
      {revising && (
        <ReviseTargetDialog
          node={revising}
          open
          onOpenChange={(open) => {
            if (!open) setRevisingId(null)
          }}
        />
      )}
    </BoardEditContext.Provider>
  )
}

function drilledKind(node: OgsmBoardNode): string {
  return node.depth === 0 ? OGSM_COPY.kind.objective : OGSM_COPY.kind.strategy
}
